import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  commonPostPutApi,
  commonPostPutFormData,
} from "../../redux/reducers/commonApiSlice";
import { add_banner_image, update_banner_image, add_banner_v4, update_banner_v4 } from "../../data/ApiEndpoints";
import {
  bannerSchema,
  tvBannerSchema,
  websiteBannerSchema,
} from "../../schema/bannerSchema";
import {
  bannerType,
  websiteBannerType,
  colorCodes,
  ctaType,
} from "../wtf-team/BannerUtils";
import CommonHeadingNBtns from "../common-components/CommonHeadingNBtns";
import CustomDropDownFilter from "../common-components/filters/CustomDropDownFilter";
import useGymsData from "../../hooks/useGymsData";
import ImageUploading from "react-images-uploading";
import useCloudFrontUpload from "../../hooks/useCloudFrontUpload";


const ManageBanners = ({
  show,
  onHide,
  isEdit,
  initialValues,
  userInfoData,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [bannerImage, setBannerImage] = useState([]);
  const [bannerTypeValue, setBannerTypeValue] = useState(
    initialValues?.schedule_type || initialValues?.type || "schedule"
  );
  const [isGlobal, setIsGlobal] = useState(initialValues?.is_global || 0);
  const [selectedGym, setSelectedGym] = useState(
    initialValues?.gym_id
      ? { label: initialValues?.gym_name || `${initialValues?.gym_id}` , value: initialValues?.gym_id }
      : ""
  );
  const [websiteMobileBanner, setWebsiteMobileBanner] = useState("");
  const [mobileBannerImage, setMobileBannerImage] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [verticalBannerImage, setVerticalBannerImage] = useState([]);
  const [horizontalDimError, setHorizontalDimError] = useState("");
  const [verticalDimError, setVerticalDimError] = useState("");
  const [horizontalDimInfo, setHorizontalDimInfo] = useState("");
  const [verticalDimInfo, setVerticalDimInfo] = useState("");
  const { uploadToCloudfront, isUploading: isUploadingMobile } =
    useCloudFrontUpload();
  const { uploadToCloudfront: uploadVerticalToCloudfront, isUploading: isUploadingVertical } =
    useCloudFrontUpload();

  // Dimension ranges for TV banners
  const TV_HORIZONTAL = { minW: 1280, maxW: 1920, minH: 720, maxH: 1080, label: "1280-1920 x 720-1080" };
  const TV_VERTICAL = { minW: 720, maxW: 1080, minH: 1280, maxH: 1920, label: "720-1080 x 1280-1920" };

  const validateDimensions = (width, height, range) => {
    if (width < range.minW || width > range.maxW || height < range.minH || height > range.maxH) {
      return `Invalid dimensions: ${width} x ${height}px. Required range: ${range.label}px`;
    }
    return "";
  };

  const isGeneral = bannerTypeValue === "general";
  const isWebsite = bannerTypeValue === "website";
  const isSchedule = bannerTypeValue === "schedule";
  const isTv = bannerTypeValue === "tv";
  const gymsOptions = useGymsData();

  // Prefill form and previews when editing with initialValues
  useEffect(() => {
    if (isEdit && initialValues) {
      // Determine banner type from various fields
      const type = (initialValues?.type || "").toString().toLowerCase();
      const scheduleType = (initialValues?.schedule_type || "").toString().toLowerCase();

      // Check for website banner - type starts with "website_" or is_website flag is set
      const isWebsiteBanner =
        initialValues?.is_website === 1 ||
        initialValues?.type === 1 ||
        initialValues?.type === "1" ||
        (typeof initialValues?.type === "string" && initialValues.type.startsWith("website_"));

      if (isWebsiteBanner) {
        setBannerTypeValue("website");
      } else if (type === "tv" || scheduleType === "tv" || initialValues?.is_tv === 1) {
        setBannerTypeValue("tv");
      } else if (type === "schedule" || scheduleType === "schedule") {
        setBannerTypeValue("schedule");
      } else if (type === "general" || scheduleType === "general") {
        setBannerTypeValue("general");
      } else {
        // Fallback: use schedule_type first, then type, then default to general
        setBannerTypeValue(scheduleType || type || "general");
      }
      
      setIsGlobal(initialValues?.is_global ?? 0);
      setSelectedGym(
        initialValues?.gym_id
          ? { label: initialValues?.gym_name || `${initialValues?.gym_id}` , value: initialValues?.gym_id }
          : ""
      );
      // Desktop image preview
      if (initialValues?.image) {
        setBannerImage([{ data_url: initialValues.image }]);
      } else {
        setBannerImage([]);
      }
      // Mobile image preview for website
      if (initialValues?.mobile_banner) {
        setMobileBannerImage([{ data_url: initialValues.mobile_banner }]);
        setWebsiteMobileBanner(initialValues.mobile_banner);
      } else {
        setMobileBannerImage([]);
        setWebsiteMobileBanner("");
      }
      // Vertical TV banner preview
      if (initialValues?.tv_banner_vertical) {
        setVerticalBannerImage([{ data_url: initialValues.tv_banner_vertical }]);
      } else {
        setVerticalBannerImage([]);
      }
    }
  }, [isEdit, initialValues]);

  // Helper to detect if this is a website banner (without relying on state)
  const detectIsWebsiteBanner = () => {
    return (
      initialValues?.is_website === 1 ||
      initialValues?.type === 1 ||
      initialValues?.type === "1" ||
      (typeof initialValues?.type === "string" && initialValues.type.startsWith("website_"))
    );
  };

  // Determine initial type value for formik
  // For website banners during edit, we need the specific banner type (e.g., "website_home_page_banner_1")
  // For other cases, use the banner category type
  const getInitialFormikType = () => {
    if (isEdit && detectIsWebsiteBanner() && initialValues?.type) {
      // For website banners, type should be the specific item name, not "website"
      return initialValues.type;
    }
    return bannerTypeValue;
  };

  // Ensure location is converted to string for consistent comparison with dropdown options
  // Also try to derive location from type if location is not available
  const getInitialLocation = () => {
    // If location is directly available, use it
    if (initialValues?.location !== undefined && initialValues?.location !== null && initialValues?.location !== "") {
      return String(initialValues.location);
    }

    // For website banners, try to derive location (cat_id) from the type field
    if (isEdit && detectIsWebsiteBanner() && initialValues?.type) {
      const typeValue = initialValues.type;
      // Find which category this type belongs to
      const matchedCategory = websiteBannerType.find((category) =>
        category.items.some((item) => item.name === typeValue)
      );
      if (matchedCategory) {
        console.log("Derived location from type:", matchedCategory.cat_id, "for type:", typeValue);
        return String(matchedCategory.cat_id);
      }
    }

    return "";
  };

  const formik = useFormik({
    initialValues: {
      type: getInitialFormikType(),
      location: getInitialLocation(),
      image: initialValues?.image || "",
      status: initialValues?.status || "active",
      is_global: isGlobal,
      mobile_banner: initialValues?.mobile_banner || "",
      schedule_type: initialValues?.schedule_type || "website",
      schedule_at: initialValues?.schedule_at || "",
      is_website: 1,
      website_schedule: 1,
      color_code: initialValues?.color_code || "",
      cta: initialValues?.cta || 0,
      cta_type: initialValues?.cta_type || "",
      cta_url: initialValues?.cta_url || "",
      tv_banner_vertical_url: initialValues?.tv_banner_vertical || "",
    },
    validationSchema: isWebsite
      ? websiteBannerSchema
      : bannerTypeValue === "tv"
      ? tvBannerSchema
      : bannerSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const method = isEdit ? "PUT" : "POST";
        const endpoint = isEdit ? update_banner_image : add_banner_image;
        const formData = new FormData();
        if (isWebsite) {
          formData.append("type", values.type);
          formData.append("location", values.location);
          formData.append("status", values.status);
          formData.append("schedule_type", values.schedule_type);
          formData.append("schedule_at", values.schedule_at);
          formData.append("is_website", 1);
          formData.append("website_schedule", 1);
          formData.append("color_code", values.color_code);
          formData.append("cta", values.cta);
          formData.append("cta_type", values.cta_type);
          formData.append("cta_url", values.cta_url);
          // Desktop image as file
          if (bannerImage[0]?.file) {
            formData.append("image", bannerImage[0].file);
          }
          formData.append("mobile_banner", values.mobile_banner);
          formData.append("validity", 1);
        } else if (bannerTypeValue === "general") {
          formData.append("type", "general");
          formData.append("location", values.location);
          formData.append("status", values.status);
          formData.append("validity", 1);
          if (bannerImage[0]?.file) {
            formData.append("image", bannerImage[0].file);
          }
        } else if (bannerTypeValue === "schedule") {
          formData.append("type", "schedule");
          formData.append("location", values.location);
          formData.append("status", values.status);
          formData.append("validity", 1);
          // schedule specifics
          formData.append("schedule_type", values.schedule_type || "website");
          formData.append("schedule_at", values.schedule_at || "");
          if (bannerImage[0]?.file) {
            formData.append("image", bannerImage[0].file);
          }
        } else if (bannerTypeValue === "tv") {
          // TV banners use V4 JSON endpoint with horizontal + vertical support
          let horizontalUrl = "";
          let verticalUrl = "";

          // Upload horizontal banner if a new file was selected
          if (bannerImage[0]?.file) {
            horizontalUrl = await uploadToCloudfront(bannerImage[0].file, userInfoData?.token);
            if (!horizontalUrl) {
              toast.error("Failed to upload horizontal banner. Please try again.");
              setLoading(false);
              return;
            }
          } else if (isEdit && initialValues?.image) {
            // Keep existing URL when editing without re-uploading
            horizontalUrl = initialValues.image;
          }

          // Upload vertical banner if a new file was selected
          if (verticalBannerImage[0]?.file) {
            verticalUrl = await uploadVerticalToCloudfront(verticalBannerImage[0].file, userInfoData?.token);
            if (!verticalUrl) {
              toast.error("Failed to upload vertical banner. Please try again.");
              setLoading(false);
              return;
            }
          } else if (isEdit && initialValues?.tv_banner_vertical) {
            // Keep existing URL when editing without re-uploading
            verticalUrl = initialValues.tv_banner_vertical;
          }

          if (!horizontalUrl && !verticalUrl) {
            toast.error("Please upload at least one banner (horizontal or vertical).");
            setLoading(false);
            return;
          }

          const tvPayload = {
            type: "tv",
            status: values.status,
            validity: 1,
            is_tv: 1,
            schedule_type: "tv",
            is_website: 0,
            website_schedule: 0,
            isRemove: 0,
            cta: 0,
            is_approved: 0,
            is_global: values.is_global,
            gym_id: values.is_global === 0 ? (selectedGym?.value || "") : "",
            location: values.location || "tv",
            url: horizontalUrl,
            tv_banner_vertical_url: verticalUrl,
          };

          if (isEdit && initialValues?.uid) {
            tvPayload.uid = initialValues.uid;
          }

          const tvEndpoint = isEdit ? update_banner_v4 : add_banner_v4;
          const tvResult = await dispatch(
            commonPostPutApi({
              method: isEdit ? "PUT" : "POST",
              endpoint: tvEndpoint,
              token: userInfoData?.token,
              payload: tvPayload,
            })
          );
          if (tvResult?.payload?.status) {
            toast.success(tvResult?.payload?.message || "Success!");
            onSuccess?.();
            onHide();
          } else {
            const errorMessage =
              tvResult?.error?.message || tvResult?.payload?.message;
            toast.error(errorMessage || "Something went wrong!");
          }
          setLoading(false);
          return;
        }
        if (isEdit && initialValues?.uid) {
          formData.append("uid", initialValues.uid);
        }
        for (let pair of formData.entries()) {
          console.log(pair[0] + ", " + pair[1]);
        }
        const result = await dispatch(
          commonPostPutFormData({
            method,
            endpoint,
            token: userInfoData?.token,
            requestBody: formData,
          })
        );
        if (result?.payload?.status) {
          toast.success(result?.payload?.message || "Success!");
          onSuccess?.();
          onHide();
        } else {
          const errorMessage =
            result?.error?.message || result?.payload?.message;
          toast.error(errorMessage || "Something went wrong!");
        }
      } catch (error) {
        toast.error("Something went wrong!");
      } finally {
        setLoading(false);
      }
    },
  });

  // Dimension requirements for website banner types
  // Desktop: 1510 x 700, Mobile: 360 x 458
  // TODO: Uncomment when dimensions are confirmed
  // const websiteBannerDimensions = {
  //   desktop: {
  //     width: 1510,
  //     height: 700,
  //   },
  //   mobile: {
  //     width: 360,
  //     height: 458,
  //   },
  // };

  const handleImageChange = (imageList) => {
    const validImages = [];
    imageList.forEach((image) => {
      const img = new Image();
      img.src = image.data_url;
      img.onload = () => {
        validImages.push(image);
        setBannerImage(validImages);
        formik.setFieldValue("image", image.data_url);
      };
    });
  };

  // Website banner image handler (dimension validation commented out for now)
  const handleWebsiteDesktopImageChange = (imageList) => {
    if (!imageList?.length) {
      setBannerImage([]);
      formik.setFieldValue("image", "");
      return;
    }

    const image = imageList[0];
    const file = image.file;

    // File size validation (5MB for images)
    if (file && file.size > 5 * 1024 * 1024) {
      toast.error("Image size should not exceed 5 MB.");
      return;
    }

    // TODO: Uncomment dimension validation when dimensions are confirmed
    // const img = new Image();
    // img.src = image.data_url;
    // img.onload = () => {
    //   const { width, height } = img;
    //   const requiredWidth = websiteBannerDimensions.desktop.width;
    //   const requiredHeight = websiteBannerDimensions.desktop.height;
    //
    //   // Validate exact dimensions
    //   if (width !== requiredWidth || height !== requiredHeight) {
    //     toast.error(
    //       `Invalid desktop banner dimensions. Required: ${requiredWidth} x ${requiredHeight}px. Your image: ${width} x ${height}px`
    //     );
    //     return;
    //   }
    //
    //   // Valid image - proceed
    //   setBannerImage([image]);
    //   formik.setFieldValue("image", image.data_url);
    //   toast.success(`Desktop banner uploaded successfully (${width} x ${height}px)`);
    // };
    //
    // img.onerror = () => {
    //   toast.error("Failed to load image. Please select a valid image file.");
    // };

    // For now, just accept the image without dimension validation
    setBannerImage([image]);
    formik.setFieldValue("image", image.data_url);
  };

  // Mobile banner image handler (dimension validation commented out for now)
  const handleMobileImageChange = async (imageList) => {
    if (!imageList?.length) {
      setMobileBannerImage([]);
      setWebsiteMobileBanner("");
      formik.setFieldValue("mobile_banner", "");
      return;
    }

    const first = imageList[0];
    const file = first?.file;

    // File size validation (5MB for images)
    if (file && file.size > 5 * 1024 * 1024) {
      toast.error("Mobile banner size should not exceed 5 MB.");
      return;
    }

    // TODO: Uncomment dimension validation when dimensions are confirmed
    // const img = new Image();
    // img.src = first.data_url;
    // img.onload = async () => {
    //   const { width, height } = img;
    //   const requiredWidth = websiteBannerDimensions.mobile.width;
    //   const requiredHeight = websiteBannerDimensions.mobile.height;
    //
    //   // Validate exact dimensions
    //   if (width !== requiredWidth || height !== requiredHeight) {
    //     toast.error(
    //       `Invalid mobile banner dimensions. Required: ${requiredWidth} x ${requiredHeight}px. Your image: ${width} x ${height}px`
    //     );
    //     return;
    //   }
    //
    //   // Valid dimensions - proceed with upload
    //   setMobileBannerImage([first]);
    //   if (file) {
    //     const url = await uploadToCloudfront(file, userInfoData?.token);
    //     setWebsiteMobileBanner(url);
    //     formik.setFieldValue("mobile_banner", url);
    //     toast.success(`Mobile banner uploaded successfully (${width} x ${height}px)`);
    //   }
    // };
    //
    // img.onerror = () => {
    //   toast.error("Failed to load mobile banner. Please select a valid image file.");
    // };

    // For now, just accept the image without dimension validation
    setMobileBannerImage([first]);
    if (file) {
      const url = await uploadToCloudfront(file, userInfoData?.token);
      setWebsiteMobileBanner(url);
      formik.setFieldValue("mobile_banner", url);
    }
  };

  console.log("form errors", formik.errors);
  console.log("form values", formik.values);
  console.log("initialValues from props:", initialValues);
  console.log("bannerTypeValue:", bannerTypeValue, "isWebsite:", isWebsite);

  return (
    <div className="createNewCTOModalContainer mod-width-50p">
      <div className="management-form-container">
        <CommonHeadingNBtns
          title={(() => {
            const typeLabel =
              bannerTypeValue === "tv"
                ? "TV Banner"
                : bannerTypeValue === "website"
                ? "Website Banner"
                : bannerTypeValue === "schedule"
                ? "Schedule Banner"
                : "Banner";
            return `${isEdit ? "Update" : "Add"} ${typeLabel}`;
          })()}
          onClose={onHide}
          btnTxt={isEdit ? "Update" : "Add"}
          onClickBtn={formik.handleSubmit}
          loading={loading}
        />
        <form
          onSubmit={formik.handleSubmit}
          className="management-form-elements form-grid form-grid-2"
        >
          {/* Type dropdown */}
          <div className="management-form-group">
            <label className="management-form-label">Select Type</label>
            <select
              className="management-form-input custom-select"
              name="type"
              value={bannerTypeValue}
              onChange={(e) => setBannerTypeValue(e.target.value)}
              disabled={isEdit}
            >
              <option value="schedule">Schedule Banner</option>
              <option value="general">General Banner</option>
              <option value="tv">TV Banner</option>
              <option value="website">Website Banner</option>
            </select>
          </div>
          {/* General Banner fields */}
          {isGeneral && (
            <>
              <div className="management-form-group">
                <label className="management-form-label">Select Page</label>
                <select
                  className={`management-form-input custom-select${
                    formik.touched.location && formik.errors.location
                      ? " error"
                      : ""
                  }`}
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isEdit}
                >
                  <option value="">Select Page</option>
                  {bannerType.map((page) => (
                    <option key={page.page} value={page.page}>
                      {page.page}
                    </option>
                  ))}
                </select>
                {formik.touched.location && formik.errors.location && (
                  <div className="management-form-error">
                    {formik.errors.location}
                  </div>
                )}
              </div>
              <div className="management-form-group">
                <label className="management-form-label">Select Banner</label>
                <ImageUploading
                  value={bannerImage}
                  onChange={handleImageChange}
                  maxNumber={1}
                  dataURLKey="data_url"
                >
                  {({ imageList, onImageUpload, onImageRemove }) => (
                    <div className="management-image-upload">
                      <button
                        type="button"
                        className="management-form-btn-outline-primary"
                        onClick={onImageUpload}
                        style={{ marginBottom: 8 }}
                      >
                        Choose Files
                      </button>
                      {imageList.map((image, index) => (
                        <div key={index} className="management-image-preview">
                          <img
                            src={image.data_url}
                            alt=""
                            style={{ cursor: "pointer" }}
                            onClick={() => setPreviewImage(image.data_url)}
                            title="Click to preview"
                          />
                          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button
                              type="button"
                              className="management-form-btn-outline-primary"
                              onClick={() => setPreviewImage(image.data_url)}
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              className="management-form-btn-danger"
                              onClick={() => {
                                const newImages = [...bannerImage];
                                newImages.splice(index, 1);
                                setBannerImage(newImages);
                                formik.setFieldValue("image", "");
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ImageUploading>
                {formik.touched.image && formik.errors.image && (
                  <div className="management-form-error">
                    {formik.errors.image}
                  </div>
                )}
              </div>
              <div className="management-form-group">
                <label className="management-form-label">Status</label>
                <select
                  className={`management-form-input custom-select${
                    formik.touched.status && formik.errors.status
                      ? " error"
                      : ""
                  }`}
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {formik.touched.status && formik.errors.status && (
                  <div className="management-form-error">
                    {formik.errors.status}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Schedule Banner fields (like General + Date/Time) */}
          {isSchedule && (
            <>
              <div className="management-form-group">
                <label className="management-form-label">Select Page</label>
                <select
                  className={`management-form-input custom-select${
                    formik.touched.location && formik.errors.location
                      ? " error"
                      : ""
                  }`}
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isEdit}
                >
                  <option value="">Select Page</option>
                  {bannerType.map((page) => (
                    <option key={page.page} value={page.page}>
                      {page.page}
                    </option>
                  ))}
                </select>
                {formik.touched.location && formik.errors.location && (
                  <div className="management-form-error">
                    {formik.errors.location}
                  </div>
                )}
              </div>
              {/* Schedule Date and Time */}
              <div className="management-form-group">
                <label className="management-form-label">Select Schedule Date</label>
                <input
                  type="date"
                  className="management-form-input"
                  value={
                    formik.values.schedule_at
                      ? formik.values.schedule_at.split(" ")[0]
                      : ""
                  }
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = formik.values.schedule_at?.split(" ")[1] || "";
                    formik.setFieldValue(
                      "schedule_at",
                      date || time ? `${date} ${time}`.trim() : ""
                    );
                  }}
                  disabled={isEdit}
                />
              </div>
              <div className="management-form-group">
                <label className="management-form-label">Select Schedule Time</label>
                <input
                  type="time"
                  className="management-form-input"
                  value={
                    formik.values.schedule_at
                      ? formik.values.schedule_at.split(" ")[1] || ""
                      : ""
                  }
                  onChange={(e) => {
                    const time = e.target.value;
                    const date = formik.values.schedule_at?.split(" ")[0] || "";
                    formik.setFieldValue(
                      "schedule_at",
                      date || time ? `${date} ${time}`.trim() : ""
                    );
                  }}
                  disabled={isEdit}
                />
              </div>
              <div className="management-form-group">
                <label className="management-form-label">Select Banner</label>
                <ImageUploading
                  value={bannerImage}
                  onChange={handleImageChange}
                  maxNumber={1}
                  dataURLKey="data_url"
                >
                  {({ imageList, onImageUpload, onImageRemove }) => (
                    <div className="management-image-upload">
                      <button
                        type="button"
                        className="management-form-btn-outline-primary"
                        onClick={onImageUpload}
                        style={{ marginBottom: 8 }}
                      >
                        Choose Files
                      </button>
                      {imageList.map((image, index) => (
                        <div key={index} className="management-image-preview">
                          <img
                            src={image.data_url}
                            alt=""
                            style={{ cursor: "pointer" }}
                            onClick={() => setPreviewImage(image.data_url)}
                            title="Click to preview"
                          />
                          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button
                              type="button"
                              className="management-form-btn-outline-primary"
                              onClick={() => setPreviewImage(image.data_url)}
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              className="management-form-btn-danger"
                              onClick={() => {
                                const newImages = [...bannerImage];
                                newImages.splice(index, 1);
                                setBannerImage(newImages);
                                formik.setFieldValue("image", "");
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ImageUploading>
                {formik.touched.image && formik.errors.image && (
                  <div className="management-form-error">
                    {formik.errors.image}
                  </div>
                )}
              </div>
              <div className="management-form-group">
                <label className="management-form-label">Status</label>
                <select
                  className={`management-form-input custom-select${
                    formik.touched.status && formik.errors.status ? " error" : ""
                  }`}
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {formik.touched.status && formik.errors.status && (
                  <div className="management-form-error">
                    {formik.errors.status}
                  </div>
                )}
              </div>
            </>
          )}

          {/* TV Banner fields */}
          {isTv && (
            <>
              <div className="management-form-group">
                <label className="management-form-label">
                  Where will it be visible?
                </label>
                <select
                  className="management-form-input custom-select"
                  value={isGlobal}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setIsGlobal(value);
                    formik.setFieldValue("is_global", value);
                  }}
                  disabled={isEdit}
                >
                  <option value={0}>Gym (Specific Gym Only)</option>
                  <option value={1}>Global (All Gyms)</option>
                </select>
              </div>
              <div className="management-form-group">
                <label className="management-form-label">Horizontal Banner (1920 x 1080)</label>
                <div className="management-image-upload">
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/webm,video/ogg"
                    style={{ display: "none" }}
                    id="tv-horizontal-upload"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 50 * 1024 * 1024) {
                        toast.error("File size should not exceed 50 MB.");
                        e.target.value = "";
                        return;
                      }
                      const dataUrl = URL.createObjectURL(file);
                      setBannerImage([{ data_url: dataUrl, file }]);
                      formik.setFieldValue("image", dataUrl);
                      setHorizontalDimError("");
                      setHorizontalDimInfo("");

                      if (file.type.startsWith("video/")) {
                        const video = document.createElement("video");
                        video.preload = "metadata";
                        video.onloadedmetadata = () => {
                          const w = video.videoWidth;
                          const h = video.videoHeight;
                          setHorizontalDimInfo(`${w} x ${h}px`);
                          const err = validateDimensions(w, h, TV_HORIZONTAL);
                          setHorizontalDimError(err);
                          URL.revokeObjectURL(video.src);
                        };
                        video.src = URL.createObjectURL(file);
                      } else {
                        const img = new Image();
                        img.onload = () => {
                          const w = img.width;
                          const h = img.height;
                          setHorizontalDimInfo(`${w} x ${h}px`);
                          const err = validateDimensions(w, h, TV_HORIZONTAL);
                          setHorizontalDimError(err);
                        };
                        img.src = dataUrl;
                      }
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    className="management-form-btn-outline-primary"
                    onClick={() => document.getElementById("tv-horizontal-upload").click()}
                    style={{ marginBottom: 8 }}
                  >
                    Choose Image or Video
                  </button>
                  {bannerImage.length > 0 && (
                    <div className="management-image-preview">
                      {bannerImage[0]?.file?.type?.startsWith("video/") ||
                       (typeof bannerImage[0]?.data_url === "string" && bannerImage[0].data_url.endsWith(".mp4")) ? (
                        <video
                          src={bannerImage[0].data_url}
                          controls
                          style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
                        />
                      ) : (
                        <img
                          src={bannerImage[0].data_url}
                          alt=""
                          style={{ cursor: "pointer", maxWidth: "100%", maxHeight: 200 }}
                          onClick={() => setPreviewImage(bannerImage[0].data_url)}
                          title="Click to preview"
                        />
                      )}
                      <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                        <button
                          type="button"
                          className="management-form-btn-danger"
                          onClick={() => {
                            setBannerImage([]);
                            formik.setFieldValue("image", "");
                            setHorizontalDimError("");
                            setHorizontalDimInfo("");
                          }}
                        >
                          Remove
                        </button>
                        {horizontalDimInfo && !horizontalDimError && (
                          <span style={{ fontSize: 12, color: "#059669", fontWeight: 500 }}>
                            {horizontalDimInfo}
                          </span>
                        )}
                      </div>
                      {horizontalDimError && (
                        <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 500, marginTop: 6, background: "#fef2f2", padding: "6px 10px", borderRadius: 6 }}>
                          {horizontalDimError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {formik.touched.image && formik.errors.image && (
                  <div className="management-form-error">
                    {formik.errors.image}
                  </div>
                )}
              </div>
              <div className="management-form-group">
                <label className="management-form-label">Vertical Banner (1080 x 1920)</label>
                <div className="management-image-upload">
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/webm,video/ogg"
                    style={{ display: "none" }}
                    id="tv-vertical-upload"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 50 * 1024 * 1024) {
                        toast.error("File size should not exceed 50 MB.");
                        e.target.value = "";
                        return;
                      }
                      const dataUrl = URL.createObjectURL(file);
                      setVerticalBannerImage([{ data_url: dataUrl, file }]);
                      formik.setFieldValue("tv_banner_vertical_url", dataUrl);
                      setVerticalDimError("");
                      setVerticalDimInfo("");

                      if (file.type.startsWith("video/")) {
                        const video = document.createElement("video");
                        video.preload = "metadata";
                        video.onloadedmetadata = () => {
                          const w = video.videoWidth;
                          const h = video.videoHeight;
                          setVerticalDimInfo(`${w} x ${h}px`);
                          const err = validateDimensions(w, h, TV_VERTICAL);
                          setVerticalDimError(err);
                          URL.revokeObjectURL(video.src);
                        };
                        video.src = URL.createObjectURL(file);
                      } else {
                        const img = new Image();
                        img.onload = () => {
                          const w = img.width;
                          const h = img.height;
                          setVerticalDimInfo(`${w} x ${h}px`);
                          const err = validateDimensions(w, h, TV_VERTICAL);
                          setVerticalDimError(err);
                        };
                        img.src = dataUrl;
                      }
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    className="management-form-btn-outline-primary"
                    onClick={() => document.getElementById("tv-vertical-upload").click()}
                    style={{ marginBottom: 8 }}
                  >
                    Choose Image or Video
                  </button>
                  {verticalBannerImage.length > 0 && (
                    <div className="management-image-preview">
                      {verticalBannerImage[0]?.file?.type?.startsWith("video/") ||
                       (typeof verticalBannerImage[0]?.data_url === "string" && verticalBannerImage[0].data_url.endsWith(".mp4")) ? (
                        <video
                          src={verticalBannerImage[0].data_url}
                          controls
                          style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
                        />
                      ) : (
                        <img
                          src={verticalBannerImage[0].data_url}
                          alt=""
                          style={{ cursor: "pointer", maxWidth: "100%", maxHeight: 200 }}
                          onClick={() => setPreviewImage(verticalBannerImage[0].data_url)}
                          title="Click to preview"
                        />
                      )}
                      <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                        <button
                          type="button"
                          className="management-form-btn-danger"
                          onClick={() => {
                            setVerticalBannerImage([]);
                            formik.setFieldValue("tv_banner_vertical_url", "");
                            setVerticalDimError("");
                            setVerticalDimInfo("");
                          }}
                        >
                          Remove
                        </button>
                        {verticalDimInfo && !verticalDimError && (
                          <span style={{ fontSize: 12, color: "#059669", fontWeight: 500 }}>
                            {verticalDimInfo}
                          </span>
                        )}
                      </div>
                      {verticalDimError && (
                        <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 500, marginTop: 6, background: "#fef2f2", padding: "6px 10px", borderRadius: 6 }}>
                          {verticalDimError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {isUploadingVertical && <div>Uploading...</div>}
              </div>
              {isGlobal === 0 && (
                <div className="management-form-group">
                  <label className="management-form-label">Select GYM</label>
                  <CustomDropDownFilter
                    name="Gym"
                    options={gymsOptions?.data || []}
                    selectedOption={selectedGym}
                    setSelectedOption={setSelectedGym}
                    disabled={isEdit}
                  />
                </div>
              )}
              <div className="management-form-group">
                <label className="management-form-label">Status</label>
                <select
                  className={`management-form-input custom-select${
                    formik.touched.status && formik.errors.status
                      ? " error"
                      : ""
                  }`}
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {formik.touched.status && formik.errors.status && (
                  <div className="management-form-error">
                    {formik.errors.status}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Website Banner fields */}
          {isWebsite && (
            <>
              <div className="management-form-group">
                <label className="management-form-label">Select Page</label>
                <select
                  className={`management-form-input custom-select${
                    formik.touched.location && formik.errors.location
                      ? " error"
                      : ""
                  }`}
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isEdit}
                >
                  <option value="">Select Page</option>
                  {websiteBannerType.map((page) => (
                    <option key={page.cat_id} value={String(page.cat_id)}>
                      {page.page}
                    </option>
                  ))}
                </select>
                {formik.touched.location && formik.errors.location && (
                  <div className="management-form-error">
                    {formik.errors.location}
                  </div>
                )}
              </div>

              {formik.values.location && (
                <div className="management-form-group">
                  <label className="management-form-label">Select Type</label>
                  <select
                    className={`management-form-input custom-select${
                      formik.touched.type && formik.errors.type ? " error" : ""
                    }`}
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isEdit}
                  >
                    <option value="">Select Type</option>
                    {websiteBannerType
                      .find((page) => String(page.cat_id) === formik.values.location)
                      ?.items.map((item) => (
                        <option key={item.name} value={item.name}>
                          {item.dispaly_name}
                        </option>
                      ))}
                  </select>
                  {formik.touched.type && formik.errors.type && (
                    <div className="management-form-error">
                      {formik.errors.type}
                    </div>
                  )}
                </div>
              )}

              <div className="management-form-group">
                <label className="management-form-label">
                  Select Banner Desktop
                </label>
                <ImageUploading
                  value={bannerImage}
                  onChange={handleWebsiteDesktopImageChange}
                  maxNumber={1}
                  dataURLKey="data_url"
                >
                  {({ imageList, onImageUpload, onImageRemove }) => (
                    <div className="management-image-upload">
                      <button
                        type="button"
                        className="management-form-btn-outline-primary"
                        onClick={onImageUpload}
                        style={{ marginBottom: 8 }}
                      >
                        Choose Files
                      </button>
                      {imageList.map((image, index) => (
                        <div key={index} className="management-image-preview">
                          <img
                            src={image.data_url}
                            alt=""
                            style={{ cursor: "pointer" }}
                            onClick={() => setPreviewImage(image.data_url)}
                            title="Click to preview"
                          />
                          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button
                              type="button"
                              className="management-form-btn-outline-primary"
                              onClick={() => setPreviewImage(image.data_url)}
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              className="management-form-btn-danger"
                              onClick={() => {
                                const newImages = [...bannerImage];
                                newImages.splice(index, 1);
                                setBannerImage(newImages);
                                formik.setFieldValue("image", "");
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ImageUploading>
                {formik.touched.image && formik.errors.image && (
                  <div className="management-form-error">
                    {formik.errors.image}
                  </div>
                )}
              </div>
              <div className="management-form-group">
                <label className="management-form-label">
                  Mobile Banner For Small Devices
                </label>
                <ImageUploading
                  value={mobileBannerImage}
                  onChange={handleMobileImageChange}
                  maxNumber={1}
                  dataURLKey="data_url"
                >
                  {({ imageList, onImageUpload }) => (
                    <div className="management-image-upload">
                      <button
                        type="button"
                        className="management-form-btn-outline-primary"
                        onClick={onImageUpload}
                        style={{ marginBottom: 8 }}
                      >
                        Choose Files
                      </button>
                      {imageList.map((image, index) => (
                        <div key={index} className="management-image-preview">
                          <img
                            src={image.data_url}
                            alt=""
                            style={{ cursor: "pointer" }}
                            onClick={() => setPreviewImage(image.data_url)}
                            title="Click to preview"
                          />
                          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button
                              type="button"
                              className="management-form-btn-outline-primary"
                              onClick={() => setPreviewImage(image.data_url)}
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              className="management-form-btn-danger"
                              onClick={() => handleMobileImageChange([])}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ImageUploading>
                {isUploadingMobile && <div>Uploading...</div>}
                {formik.touched.mobile_banner &&
                  formik.errors.mobile_banner && (
                    <div className="management-form-error">
                      {formik.errors.mobile_banner}
                    </div>
                  )}
              </div>
              {/* CTA Type Dropdown */}
              <div className="management-form-group">
                <label className="management-form-label">
                  Select CTA Type (optional)
                </label>
                <select
                  className="management-form-input custom-select"
                  name="cta_type"
                  value={formik.values.cta_type}
                  onChange={formik.handleChange}
                  disabled={isEdit}
                >
                  <option value="">CTA Type</option>
                  {ctaType.map((cta) => (
                    <option key={cta.value} value={cta.value}>
                      {cta.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Button Color */}
              <div className="management-form-group">
                <label className="management-form-label">Button Color (optional)</label>
                <select
                  className="management-form-input custom-select"
                  name="color_code"
                  value={formik.values.color_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select Color</option>
                  {colorCodes.map((color) => (
                    <option key={color.color_code} value={color.color_code}>
                      {color.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Schedule Date and Time */}
              <div className="management-form-group">
                <label className="management-form-label">
                  Select Schedule Date (optional)
                </label>
                <input
                  type="date"
                  className="management-form-input"
                  value={
                    formik.values.schedule_at
                      ? formik.values.schedule_at.split(" ")[0]
                      : ""
                  }
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = formik.values.schedule_at?.split(" ")[1] || "";
                    formik.setFieldValue(
                      "schedule_at",
                      date || time ? `${date} ${time}`.trim() : ""
                    );
                  }}
                  disabled={isEdit}
                />
              </div>
              <div className="management-form-group">
                <label className="management-form-label">
                  Select Schedule Time (optional)
                </label>
                <input
                  type="time"
                  className="management-form-input"
                  value={
                    formik.values.schedule_at
                      ? formik.values.schedule_at.split(" ")[1] || ""
                      : ""
                  }
                  onChange={(e) => {
                    const time = e.target.value;
                    const date = formik.values.schedule_at?.split(" ")[0] || "";
                    formik.setFieldValue(
                      "schedule_at",
                      date || time ? `${date} ${time}`.trim() : ""
                    );
                  }}
                  disabled={isEdit}
                />
                {formik.touched.schedule_at && formik.errors.schedule_at && (
                  <div className="management-form-error">
                    {formik.errors.schedule_at}
                  </div>
                )}
              </div>
            </>
          )}
          <div className="management-form-group">
            <button
              type="submit"
              className="management-form-btn-danger"
              disabled={loading || (isTv && (!!horizontalDimError || !!verticalDimError))}
            >
              {loading ? "Uploading..." : isEdit ? "Update" : "Upload"}
            </button>
          </div>
        </form>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: 20,
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              style={{
                position: "absolute",
                top: -40,
                right: 0,
                background: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 32,
                height: 32,
                fontSize: 20,
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
            >
              &times;
            </button>
            <img
              src={previewImage}
              alt="Banner Preview"
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: 8,
                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBanners;
